import { useEffect, useRef, type ReactNode } from 'react';
import { useStableId } from '@repletoryreach/sdk-utils/hooks';
import { ariaProps } from '@repletoryreach/sdk-utils/a11y';
import { trapFocus, lockBodyScroll, unlockBodyScroll } from '@repletoryreach/sdk-utils/dom';
import { Portal } from '../../core/Portal/Portal.js';
import { ScrollArea } from '../../scroll/ScrollArea.js';
import { CloseButton } from '../../core/CloseButton/CloseButton.js';
import { Overlay } from '../Overlay/Overlay.js';
import { useReducedMotionMultiplier } from '../../motion/hooks/useReducedMotionMultiplier.js';
import './Modal.css';

export interface ModalProps {
  opened: boolean;
  onClose: () => void;
  /** Visible title: rendered as the `<h2>` inside the modal header. */
  title?: ReactNode;
  children?: ReactNode;
  /** Modal width: preset OR raw CSS. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | string;
  /** Center modal vertically. Default: true. */
  centered?: boolean;
  /** Close when clicking the backdrop. Default: true. */
  closeOnClickOutside?: boolean;
  /** Close when pressing Escape. Default: true. */
  closeOnEscape?: boolean;
  /** Show the × button. Default: true. */
  withCloseButton?: boolean;
  /** Render full-screen (mobile-friendly). */
  fullScreen?: boolean;
  /** Disable focus trap (rare). */
  trapFocus?: boolean;
  /** Custom z-index. */
  zIndex?: number;
  /** Custom Portal target. */
  portalTarget?: HTMLElement | string;
  /** ClassName applied to the inner panel. */
  className?: string;
  /** Override overlay props (color, opacity, blur). */
  overlayProps?: { color?: string; backgroundOpacity?: number; blur?: number };
  /**
   * Id of an element inside the modal that names it, for a modal with no
   * `title` (a result popup whose heading sits under an icon). A dialog needs a
   * name for screen readers; with `title` set, the title is that name.
   */
  labelledBy?: string;
  /** Id of an element inside the modal that describes it. */
  describedBy?: string;
}

const SIZE_PX: Record<string, string> = {
  xs: '320px', sm: '440px', md: '560px', lg: '720px', xl: '960px', full: '100%'
};

/**
 * Modal dialog. Locks body scroll, traps focus, returns focus to the
 * opener on close, and honors `useReducedMotion` for the entrance/exit
 * animation.
 */
export function Modal({
  opened,
  onClose,
  title,
  children,
  size = 'md',
  centered = true,
  closeOnClickOutside = true,
  closeOnEscape = true,
  withCloseButton = true,
  fullScreen,
  trapFocus: shouldTrap = true,
  zIndex,
  portalTarget,
  className,
  overlayProps,
  labelledBy,
  describedBy
}: ModalProps): ReactNode {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = useStableId('rr-modal-title');
  const multiplier = useReducedMotionMultiplier();

  // Capture the previously-focused element, lock body scroll, install
  // focus trap; restore on close.
  useEffect(() => {
    if (!opened) return;
    openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    lockBodyScroll();
    const cleanupTrap = shouldTrap && panelRef.current ? trapFocus(panelRef.current) : () => {};
    return () => {
      cleanupTrap();
      unlockBodyScroll();
      openerRef.current?.focus?.();
    };
  }, [opened, shouldTrap]);

  // Escape to close.
  useEffect(() => {
    if (!opened || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [opened, closeOnEscape, onClose]);

  if (!opened) return null;

  const width = fullScreen ? '100vw' : (SIZE_PX[size] ?? size);
  const height = fullScreen ? '100vh' : undefined;

  return (
    <Portal target={portalTarget}>
      <div
        className={`rr-modal-root ${centered ? 'rr-modal-root--centered' : ''} ${fullScreen ? 'rr-modal-root--full' : ''}`}
        style={{ zIndex: zIndex ?? 'var(--rr-z-modal)' as unknown as number }}
      >
        <Overlay
          color={overlayProps?.color}
          backgroundOpacity={overlayProps?.backgroundOpacity ?? 0.45}
          blur={overlayProps?.blur ?? 6}
          onClick={() => closeOnClickOutside && onClose()}
        />
        <div
          ref={panelRef}
          className={['rr-modal', fullScreen && 'rr-modal--full', className].filter(Boolean).join(' ')}
          {...ariaProps.dialog({ labelledBy: title ? titleId : labelledBy, describedBy })}
          style={{
            width, height, maxWidth: '100%', maxHeight: '100%',
            animationDuration: multiplier === 0 ? '0ms' : undefined
          }}
        >
          {(title || withCloseButton) && (
            <div className="rr-modal__header">
              {title ? <h2 id={titleId} className="rr-modal__title">{title}</h2> : <span />}
              {withCloseButton ? <CloseButton onClick={onClose} /> : null}
            </div>
          )}
          <ScrollArea className="rr-modal__body" viewportClassName="rr-modal__body-viewport" overscroll="contain">{children}</ScrollArea>
        </div>
      </div>
    </Portal>
  );
}
